/**
 * The feed panel: the visitor burns OBRAIN and the brain thinks about it.
 *
 * The chain side of the panel - which token, which dead address, what each tier
 * costs - arrives as `FeedState`, read from the brain contract through the
 * indexer, so no address or price is remembered here. The wallet side (balance,
 * allowance, the two transactions the visitor signs) goes through feed.ts, the
 * one module allowed to touch window.ethereum. The decisions in between are
 * feed-flow.ts's pure rules. What is left for this file is only: read, ask,
 * render, repeat.
 *
 * Nothing here signs by itself: every write hands calldata to the wallet and
 * waits for the visitor to approve it in their own extension.
 */

import type { Feed, FeedState } from "./api";

import { fetchHistory } from "./api";
import { allowance, approve, balanceOf, feed, shortAddress } from "./feed";
import {
  allowanceNeeded,
  escapeHtml,
  feedConfirmation,
  feedReadiness,
  tierBurnAmount,
  TIER_IDS,
  type Readiness,
  type TierId,
} from "./feed-flow";
import { t, type TKey } from "./i18n";

/**
 * The kernel reads its sensory input as `(n % 60) * 3`, so exactly 60 channels
 * exist (src/Obrain.sol CHANNELS). The contract is sealed, so the count is a
 * constant; feed() reverts BadChannel() for anything above it.
 */
const CHANNELS = 60;

/**
 * The sense this feed will touch. The visitor never picks it: the panel draws
 * one per feed and names it before the press, so feeding stays one click and
 * never a form. Not a security draw - any channel serves as well as another.
 */
function drawChannel(): number {
  return Math.floor(Math.random() * CHANNELS);
}

/**
 * What each tier charges one channel, as the kernel multipliers of src/Obrain.sol
 * `levels` (31 / 127 / 255) against a firing threshold of 200 * 64. Shown to
 * explain why a bigger burn is a different event, not just a bigger number.
 */
const CHARGE: Readonly<Record<TierId, TKey>> = {
  0: "feed.charge0",
  1: "feed.charge1",
  2: "feed.charge2",
};

const ONE_TOKEN = 10n ** 18n;

/** About a minute of waiting for the indexer to catch up with the burn. */
const CONFIRM_ROUNDS = 30;

const CONFIRM_EVERY_MS = 2_000;

/**
 * How much of the feed log the rail keeps in its list: the newest burns, with
 * the list scrolling inside the rail for the rest. More than a screen's worth
 * would only be a longer scroll, so this is what the rail can show and a little
 * past it.
 */
const LOG_ROWS = 24;

/** Whole OBRAIN, grouped: tiers and balances are whole tokens, dust and all. */
function tokens(amount: bigint): string {
  return (amount / ONE_TOKEN).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** Parses a tier out of the DOM; the only place a string becomes a TierId. */
function tierIdOf(value: string | undefined): TierId | null {
  if (value === "0") return 0;

  if (value === "1") return 1;

  if (value === "2") return 2;

  return null;
}

function must<T extends HTMLElement>(id: string, tag: new () => T): T {
  const el = document.getElementById(id);

  if (!(el instanceof tag)) throw new Error(`feed panel: missing #${id}`);

  return el;
}

export type FeedPanelInput = {
  /** the brain: the feed target, and the address the OBRAIN token approves */
  readonly brain: string;
  /**
   * The brain's own report of its token, dead address and tier prices, as soon
   * as the indexer has answered. Null only while that read is still in flight:
   * the panel is always on the page, and always says which state it is in.
   */
  readonly chain: FeedState | null;
  /** the visitor's connected address, or null while disconnected */
  readonly wallet: () => string | null;
  /** the chain explorer's base URL: every log row links to its own burn */
  readonly explorer: string;
  /** after a burn lands: repaint the field and the burned total */
  /**
   * The burn is onchain. The panel hands back what it paid for - the channel and
   * the tier, and the transaction that carries both - so the rest of the view
   * can show the poke and then match the thought the burn opened.
   */
  readonly onBurned: (burn: { channel: number; tier: number; txHash: string }) => void;
};

export type FeedPanel = {
  /** the wallet changed: re-read what the panel needs from it */
  readonly walletChanged: () => void;
  /** the brain's report arrived: prices, the dead address and the token */
  readonly setChain: (chain: FeedState) => void;
  /** the indexer's own feed history, oldest first: seeds the log */
  readonly setLog: (feeds: readonly Feed[]) => void;
  /** a feed landed live: it goes to the top of the log */
  readonly pushFeed: (feed: Feed) => void;
  /** the language switched: repaint this panel's own words */
  readonly relanguage: () => void;
};

export function mountFeedPanel(input: FeedPanelInput): FeedPanel {
  const channelOutEl = must("feedChannelOut", HTMLElement);
  const tiersEl = must("feedTiers", HTMLDivElement);
  const burnEl = must("feedBurn", HTMLElement);
  const balanceEl = must("feedBalance", HTMLElement);
  const chargeEl = must("feedCharge", HTMLElement);
  const goEl = must("feedGo", HTMLButtonElement);
  const noteEl = must("feedNote", HTMLElement);
  const logEl = must("feedLogList", HTMLOListElement);

  let chain = input.chain;
  let tier: TierId = 0;
  let channel = drawChannel();

  let balance: bigint | null = null;
  let granted: bigint | null = null;
  let reading = false;
  let readError: string | null = null;

  let busy = false;
  let note: string | null = null;

  // the chain's own feed history, newest first: setLog seeds it from the
  // indexer's walk, pushFeed keeps it live from the stream
  let log: readonly Feed[] = [];

  // ---- the static furniture, built from the chain's own prices -----------

  // one button per tier, labelled and priced by the brain itself
  const tierButtons = TIER_IDS.map((id) => {
    const button = document.createElement("button");

    button.type = "button";
    button.className = "feed-tier";
    button.dataset.tier = String(id);
    button.dataset.testid = `feed-tier-${id}`;
    button.setAttribute("role", "radio");
    tiersEl.append(button);

    return button;
  });

  // ---- the feed log: the chain's own record of burns ----------------------

  function renderLog(): void {
    if (log.length === 0) {
      logEl.replaceChildren();

      const empty = document.createElement("li");

      empty.className = "feed-empty";
      empty.textContent = t("feed.logEmpty");
      logEl.append(empty);

      return;
    }

    logEl.replaceChildren(
      ...log.slice(0, LOG_ROWS).map((feed) => {
        const row = document.createElement("li");
        const link = document.createElement("a");
        const what = document.createElement("span");
        const who = document.createElement("span");

        what.innerHTML = t("feed.logRow", {
          n: `<b>${tokens(BigInt(feed.amount))}</b>`,
          c: String(feed.channel),
        });
        who.className = "feed-who";
        who.textContent = shortAddress(feed.feeder);
        who.title = feed.feeder;

        // the chain's own record of the burn: the row opens its transaction
        link.className = "feed-tx";
        link.href = `${input.explorer}/tx/${feed.txHash}`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.title = feed.txHash;
        link.append(what, who);

        row.dataset.testid = "feed-log-row";
        row.append(link);

        return row;
      }),
    );
  }

  // ---- rendering ----------------------------------------------------------

  const amountNow = (): bigint | null =>
    chain === null ? null : tierBurnAmount(chain.tierAmounts, tier);

  const readinessNow = (): Readiness | null => {
    const amount = amountNow();

    if (amount === null) return null; // the brain has not reported its prices yet

    return feedReadiness({
      balance: input.wallet() === null ? null : balance,
      allowance: granted,
      amount,
    });
  };

  /** The panel's whole voice: what happened, or what is still missing. */
  function noteFor(readiness: Readiness | null, amount: bigint | null): string {
    if (readiness === null || amount === null) return t("feed.readChain");

    if (readError !== null && input.wallet() !== null) {
      return t("feed.readFailed", { reason: escapeHtml(readError) });
    }

    switch (readiness.kind) {
      case "insufficient":
        return t("feed.insufficient", { n: tokens(readiness.short) });
      case "needs-approval":
        return t("feed.grant");
      case "ready":
        return reading ? t("feed.reading") : t("feed.readyNote", { n: tokens(amount) });
      case "no-wallet":
        return t("feed.noWallet");
    }
  }

  function labelFor(readiness: Readiness | null): string {
    if (busy) return t("feed.confirm");

    return readiness?.kind === "needs-approval" ? t("feed.approve") : t("feed.ready");
  }

  function render(): void {
    const amount = amountNow();

    burnEl.textContent = amount === null ? "-" : tokens(amount);
    chargeEl.textContent = t(CHARGE[tier]);
    balanceEl.textContent = balance === null ? "-" : tokens(balance);
    channelOutEl.textContent = String(channel);

    for (const button of tierButtons) {
      const id = tierIdOf(button.dataset.tier);
      const tiers = chain === null ? null : chain.tierAmounts;

      // the chain's own prices, or the same dash the burn line shows while the
      // read is still out: an empty button reads as a broken panel
      button.textContent = id === null || tiers === null ? "-" : tokens(tierBurnAmount(tiers, id));

      const mine = button.dataset.tier === String(tier);

      button.classList.toggle("on", mine);
      button.setAttribute("aria-checked", mine ? "true" : "false");
      button.tabIndex = mine ? 0 : -1;
    }

    const readiness = readinessNow();

    noteEl.innerHTML = note ?? noteFor(readiness, amount);

    // The site has one connect, the nav's wallet button, so a visitor without a
    // wallet gets no second one here - the panel only says what is missing.
    goEl.hidden = input.wallet() === null;

    // the only disabled state is "this cannot work yet": approving has a button
    // to press, and a wallet that is short of OBRAIN buys on the market (feed-buy)
    goEl.disabled = busy || readiness === null || readiness.kind === "insufficient";
    goEl.textContent = labelFor(readiness);
  }

  // ---- the wallet's numbers ----------------------------------------------

  /** One retry: the public Arc node rate-limits under bursts. */
  async function readWallet(): Promise<void> {
    const owner = input.wallet();

    if (owner === null) {
      balance = null;
      granted = null;
      readError = null;
      render();

      return;
    }

    reading = true;
    readError = null;
    render();

    const token = chain?.token;

    if (token === undefined) {
      // the brain has not reported its token yet; setChain re-reads when it has
      reading = false;
      render();

      return;
    }

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const [held, approved] = await Promise.all([
          balanceOf(owner, token),
          allowance(owner, input.brain, token),
        ]);

        balance = held;
        granted = approved;

        break;
      } catch (error) {
        readError = error instanceof Error ? error.message : t("feed.unknownError");

        if (attempt === 0) await new Promise((resolve) => window.setTimeout(resolve, 1_500));
      }
    }

    reading = false;
    render();
  }

  /**
   * The burn is mined before its thought is indexed, so this is a bounded wait
   * for the indexer to catch up - never a re-read of the chain. When the wait
   * runs out, the panel says the burn landed and stops guessing.
   */
  async function awaitThought(hash: string): Promise<void> {
    for (let round = 0; round < CONFIRM_ROUNDS; round += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, CONFIRM_EVERY_MS));

      const history = await fetchHistory().catch(() => null);

      if (history === null) continue;

      const found = feedConfirmation(history.feeds, history.ticks, hash);

      if (found === null) continue;

      note =
        found.fired === null
          ? t("feed.thoughtUndecoded", { tick: String(found.tick) })
          : t("feed.thought", { tick: String(found.tick), fired: String(found.fired) });

      render();

      return;
    }

    note = t("feed.landed");
    render();
  }

  // ---- the two writes the visitor signs ----------------------------------

  async function primary(): Promise<void> {
    // a second click while a wallet prompt is open would be a second burn
    if (busy) return;

    const readiness = readinessNow();

    if (readiness === null || chain === null) return;

    if (readiness.kind !== "needs-approval" && readiness.kind !== "ready") return;

    const owner = input.wallet();

    if (owner === null) return;

    busy = true;

    try {
      if (readiness.kind === "needs-approval") {
        note = t("feed.approving");
        render();

        await approve(owner, chain.token, input.brain, allowanceNeeded());

        note = t("feed.grant");
      } else {
        note = t("feed.feeding", { n: tokens(tierBurnAmount(chain.tierAmounts, tier)) });
        render();

        const hash = await feed(owner, input.brain, channel, tier);

        // the burn is onchain: release the button and let the field react
        // while the indexer catches up with the thought it opened
        busy = false;
        note = t("feed.pending");
        input.onBurned({ channel, tier, txHash: hash });
        render();

        await awaitThought(hash);

        // the fee is paid: the next press touches a fresh sense
        channel = drawChannel();
        render();

        await readWallet();

        return;
      }
    } catch (error) {
      note = t("feed.failed", {
        reason: escapeHtml(error instanceof Error ? error.message : t("feed.unknownError")),
      });
    }

    busy = false;
    render();

    await readWallet();
  }

  // ---- wiring -------------------------------------------------------------

  tiersEl.addEventListener("click", (event) => {
    const target = event.target instanceof HTMLElement ? event.target.closest(".feed-tier") : null;

    if (!(target instanceof HTMLElement) || busy) return;

    const next = tierIdOf(target.dataset.tier);

    if (next === null) return;

    tier = next;
    note = null;
    render();
  });

  goEl.addEventListener("click", () => {
    void primary();
  });

  // A visitor who leaves to buy OBRAIN and comes back must not be told they
  // still have none: re-read the wallet whenever the page is looked at again.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && input.wallet() !== null) void readWallet();
  });

  render();
  renderLog();

  return {
    walletChanged: () => {
      note = null;
      void readWallet();
    },
    /** the indexer's own walk of the feed events, oldest first */
    setLog: (feeds: readonly Feed[]) => {
      log = [...feeds].reverse();
      renderLog();
    },
    /** a feed just landed on chain: it goes to the top of the log */
    pushFeed: (feed: Feed) => {
      if (log[0]?.txHash === feed.txHash) return; // a replayed stream event

      log = [feed, ...log];
      renderLog();
    },
    relanguage: () => {
      render();
      renderLog();
    },
    setChain: (state: FeedState) => {
      const first = chain === null;

      chain = state;
      render();

      // a wallet connected before the prices arrived still needs its numbers
      if (first && input.wallet() !== null) void readWallet();
    },
  };
}
